import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import type { Coordinate } from '@sembo-private/maps';
import { useWindowSize } from '@vueuse/core';

import { RecipeStateKey } from '@apps/main/modules/state/state';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { injectStrict } from '@libs/utils';
import {
  Breakpoints,
  getIsSmallDevice,
  isLargerThan,
  isSmallerThan,
} from '@libs/ui';

const { t } = i18n.global;

// NOTE:
// Purpose of this file is to hold utilities related to browser capabilities and nothing else.

export const useBrowserStore = defineStore('BrowserStore', () => {
  // State
  const geolocationApiCoordinates = ref<Coordinate | undefined>(undefined);
  const recipeState = injectStrict(RecipeStateKey);
  const premadeTripTitle = ref<string | undefined>(undefined);
  const { width } = useWindowSize();

  // Internal state
  let previousTitle = '';

  // NOTE: premade trip title has always priority over hotel ingredients
  watch(
    [recipeState.hotelOnlyRecipe, premadeTripTitle],
    ([hotelIngredients, premadeTripTitle]) => {
      if (premadeTripTitle) {
        if (previousTitle !== premadeTripTitle) {
          setTitle(premadeTripTitle);
          previousTitle = premadeTripTitle;
        }
        return;
      }

      if (hotelIngredients.length > 0) {
        const title = t('planner.page_title');
        const locations = hotelIngredients
          .map((hotel) => hotel.displayText)
          .join(' → ');

        const newTitle = `${title} ${locations}`;
        if (previousTitle !== newTitle) {
          setTitle(newTitle);
          previousTitle = newTitle;
        }

        return;
      }
    },
    { immediate: true }
  );

  function setTitle(title: string): void {
    document.title = title;
  }

  // Actions
  function loadUserCoordinatesFromGeolocationAPI(): Promise<
    Coordinate | undefined
  > {
    return new Promise<Coordinate | undefined>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const geolocationPosition: Coordinate = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };

          resolve(geolocationPosition);
        },
        () => resolve(undefined)
      );
    });
  }

  function setSessionStorageItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  function getSessionStorageItem(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  function setPremadeTripTitle(title: string): void {
    premadeTripTitle.value = title;
  }

  // Getters
  /**
   * @deprecated Use isSmallerThanLargeMin new media query getter instead
   */
  const isSmallDevice = computed(() => getIsSmallDevice());

  const isLargerThanExtraSmallMax = computed(() =>
    isLargerThan(width.value, Breakpoints.ExtraSmallMax)
  );

  const isLargerThanMediumMax = computed(() =>
    isLargerThan(width.value, Breakpoints.MediumMax)
  );

  const isSmallerThanSmallMin = computed(() =>
    isSmallerThan(width.value, Breakpoints.SmallMin)
  );

  const isSmallerThanLargeMin = computed(() =>
    isSmallerThan(width.value, Breakpoints.LargeMin)
  );

  const isLargerThanLargeMax = computed(() =>
    isLargerThan(width.value, Breakpoints.LargeMax)
  );

  const isLargerThanSmallMin = computed(() =>
    isLargerThan(width.value, Breakpoints.SmallMin)
  );

  const isLargerThanSmallMax = computed(() =>
    isLargerThan(width.value, Breakpoints.SmallMax)
  );

  const isSmallerThanMediumMin = computed(() =>
    isSmallerThan(width.value, Breakpoints.MediumMin)
  );

  return {
    // State
    geolocationApiCoordinates,

    // Actions
    loadUserCoordinatesFromGeolocationAPI,
    setSessionStorageItem,
    getSessionStorageItem,
    setPremadeTripTitle,

    // Getters
    /**
     * @deprecated Use isSmallerThanLargeMin new media query getter instead
     */
    isSmallDevice,
    isLargerThanExtraSmallMax,
    isLargerThanMediumMax,
    isSmallerThanSmallMin,
    isLargerThanLargeMax,
    isLargerThanSmallMin,
    isLargerThanSmallMax,
    isSmallerThanMediumMin,
    isSmallerThanLargeMin,
  };
});
